import { InitiativesService } from './../../../../@core/utils/initiatives.service';
import { ChartsService } from './../../../../@core/utils/charts.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { APIService, KPI } from '../../../../@core/api';
import { NbDialogRef, NbDialogService, NbCardModule, NbSpinnerModule, NbSelectModule, NbOptionModule, NbInputModule, NbButtonModule } from '@nebular/theme';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ErrorService, PerimetersService, ScopeService } from '../../../../@core/utils';
import { AsyncPipe } from '@angular/common';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

@Component({
    selector: 'app-add-consumption-dialog',
    templateUrl: './add-consumption-dialog.component.html',
    styleUrls: ['./add-consumption-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbSelectModule,
    NbOptionModule,
    NbInputModule,
    NbButtonModule,
    AsyncPipe,
    TranslateModule
],
})
export class AddConsumptionDialogComponent implements OnInit, OnDestroy {

  @Input() public energyType: KPI.InitiativeEnergyType = null;
  @Input() public multiPerimeter: KPI.Perimeter;
  @Input() public consumption: KPI.PerimeterTrajectoryConsumption = null;
  @Input() public isEditing: boolean = false;

  public loading = false;

  private subscriptions: Subscription[] = [];

  private readonly NUMBER_VALIDATORS = [Validators.required, Validators.pattern(/^[0-9]*$/)];
  private lastYear = new Date().getFullYear() - 1;
  private consumptionFields: KPI.TConsumptionType[] = ['consumption', 'consumption_corrected_climate', 'consumption_corrected_official'];
  public form = new UntypedFormGroup({
    site: new UntypedFormControl({ value: '', disabled: true}, [Validators.required]),
    perimeter: new UntypedFormControl(null, [Validators.required]),
    ...this.consumptionFields.reduce((acc, field) => ({
      ...acc,
      [field]: new UntypedFormControl(0, this.NUMBER_VALIDATORS),
    }), {}),
    year: new UntypedFormControl(
      this.lastYear,
      [...this.NUMBER_VALIDATORS, Validators.min(1900), Validators.max(this.lastYear)],
    ),
    energy_type: new UntypedFormControl(null, Validators.required),
    comment: new UntypedFormControl(''),
  });

  public perimeters$: Observable<KPI.PerimeterConsumption[]>;

  public energyTypes$: Observable<KPI.InitiativeEnergyType[]> = this.initiativesService.initiativeEnergyTypes$;

  constructor(
    private dialogRef: NbDialogRef<AddConsumptionDialogComponent>,
    private api: APIService,
    private chartsService: ChartsService,
    private dialogService: NbDialogService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private errorService: ErrorService,
    private initiativesService: InitiativesService,
    private perimetersService: PerimetersService,
    private scopeService: ScopeService,
  ) { }

  public ngOnInit(): void {
    this.form.get('site').setValue(this.multiPerimeter.name);
    this.form.get('energy_type').setValue(this.energyType?.id);

    if (this.isEditing) {
      this.form.get('comment').setValue(this.consumption.comment);
      this.form.get('year').setValue(this.consumption.year);
      this.form.get('year').disable();
      this.form.get('energy_type').disable();
      this.form.get('perimeter').setValue(this.consumption.perimeter?.id);
      this.form.get('perimeter').disable();
    }

    this.perimeters$ = combineLatest([
      this.perimetersService.perimeters$,
      this.chartsService.getSiteConsumption(this.multiPerimeter.id),
      this.scopeService.perimeterTypes$,
    ]).pipe(
      map(([perimeters, siteConsumption, perimeterTypes]) =>
        siteConsumption?.perimeters.filter((perimeter) => {
          const perimeterTypeId: number = perimeters
            .reduce((acc, p) => ([...acc, ...p.sub_perimeters]), [] as KPI.Perimeter[])
            .find((p) => p.id === perimeter.id)?.perimeter_type;
          return (!this.energyType || perimeter.trajectories?.find((trajectory) => trajectory.energy_type?.id === this.energyType?.id))
            && perimeterTypes[perimeterTypeId]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING;
        }),
      ),
    );

    // Set perimeter to the only one available, if adding a new consumption
    this.perimeters$.pipe(
      first(),
      filter((perimeters) => !this.isEditing && perimeters?.length === 1),
    ).subscribe((perimeters) =>
      this.form.get('perimeter').setValue(perimeters[0].id),
    );

    this.consumptionFields.forEach((currentField, i) => {
      this.form.get(currentField).setValidators([
        ...this.NUMBER_VALIDATORS,
        this.validateConsumption(this.form),
      ]);

      this.form.get(currentField).setValue(this.consumption?.[currentField] ?? 0);

      this.form.get(currentField).valueChanges.subscribe(() => {
        this.consumptionFields.forEach((otherField, y) => {
          if (i !== y) {
            this.form.get(otherField).updateValueAndValidity({emitEvent: false});
          }
        });
      });
    });
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.dialogRef.close();
  }

  // Check that at least one consumption is filled
  private validateConsumption(form: UntypedFormGroup): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const atLeastOneConsumption = this.consumptionFields.some((field) =>
        form.get(field).value !== null && form.get(field).value > 0
      );
      return !atLeastOneConsumption ? { consumption: { value: control.value } } : null;
    };
  }

  public onSubmit(): void {
    this.loading = true;
    this.cdRef.detectChanges();
    this.existingConsumptionId$.pipe(
      first()
    ).subscribe((existingConsumption) => {
      if (!!existingConsumption) {
        if (!this.isEditing) {
          const dialog = this.dialogService.open(
            BasicDialogComponent,
            {
              autoFocus: false,
              context: {
                title: this.translate.instant('Consumption modification'),
                content: this.translate.instant(
                  'A consumption for {year} already exists for this perimeter Do you want to replace it ?',
                  { year: this.form.get('year').value }
                ),
              }
            }
          );

          dialog.onClose.pipe(
            filter((replace) => !!replace),
            switchMap(() => this.updateConsumption()),
          ).subscribe(() => { }, (err) => {
            this.loading = false;
            this.cdRef.detectChanges();
            this.errorService.handleError(err, true);
          });

          dialog.onClose.pipe(
            filter((replace) => !replace),
          ).subscribe(() => {
            this.loading = false;
            this.cdRef.detectChanges();
          });
        } else {
          this.updateConsumption().subscribe(() => { }, (err) => {
            this.loading = false;
            this.cdRef.detectChanges();
            this.errorService.handleError(err, true);
          });
        }
      } else {
        this.addConsumption().subscribe(() => { }, (err) => {
          this.loading = false;
          this.cdRef.detectChanges();
          this.errorService.handleError(err, true);
        });
      }
    });
  }

  private get existingConsumptionId$(): Observable<number> {
    return combineLatest([
      this.perimeters$.pipe(first()),
      this.form.get('perimeter').valueChanges.pipe(
        startWith(this.form.get('perimeter').value)
      ),
      this.form.get('energy_type').valueChanges.pipe(
        startWith(this.form.get('energy_type').value)
      ),
    ]).pipe(
      first(),
      map(([perimeters, perimeter, energyType]) => {
        let extistingConsumptionId: number = null;
        perimeters.filter((p) =>
          p.id === perimeter
        ).forEach((p) =>
          p.trajectories?.filter((t) =>
            t.energy_type?.id === energyType
          ).forEach((trajectory) => {
            const existingConsumption: KPI.PerimeterTrajectoryConsumption =
              trajectory.perimeter_consumptions?.find((consumption) =>
                consumption.year === this.form.get('year').value
                && consumption.consumption !== null
              );
            if (existingConsumption) {
              extistingConsumptionId = existingConsumption.id;
            }
          })
        )
        return extistingConsumptionId;
      })
    );
  }

  private addConsumption(): Observable<{}> {
    return this.api.add_year_consumption({
      perimeter_id: this.form.get('perimeter').value,
      energy_type_id: this.form.get('energy_type').value,
      year: this.form.get('year').value,
      consumption: this.form.get('consumption').value,
      consumption_corrected_climate: this.form.get('consumption_corrected_climate').value,
      consumption_corrected_official: this.form.get('consumption_corrected_official').value,
      comment: this.form.get('comment').value,
    }).pipe(
      first(),
      tap(() => {
        this.chartsService.declaredConsumptionsHasChanged();
        this.loading = false;
        this.cdRef.detectChanges();
        this.dialogRef.close(true);
      }),
    );
  }

  private updateConsumption(): Observable<{}> {
    return this.existingConsumptionId$.pipe(
      switchMap((existingConsumptionId) => this.api.update_year_consumption(
        existingConsumptionId,
        {
          perimeter_id: this.form.get('perimeter').value,
          energy_type_id: this.form.get('energy_type').value,
          year: this.form.get('year').value,
          consumption: this.form.get('consumption').value,
          consumption_corrected_climate: this.form.get('consumption_corrected_climate').value,
          consumption_corrected_official: this.form.get('consumption_corrected_official').value,
          comment: this.form.get('comment').value,
        })),
      first(),
      tap(() => {
        this.chartsService.declaredConsumptionsHasChanged();
        this.loading = false;
        this.cdRef.detectChanges();
        this.dialogRef.close(true);
      }),
    );
  }

  public isMissingAnyConsumptionError(): boolean {
    return this.consumptionFields.some((field) =>
      this.form.get(field).errors?.['consumption'],
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
